import { camelize } from 'vue';

import { filterEmpty, omit, warn } from '@components/utils';

import type { ITreeNodeProps } from '@components/components/CdsTree/composable/treeNode';
import type { NTree } from '../interface';
import type { ITreeProps } from '../composable';
import type { VueNode } from '@components/utils';

type TExternalGetKey = GetKey<NTree.IDataNode> | string;

interface IWrapper {
  posEntities: Record<string, any>;
  keyEntities: Record<any, any>;
}

export type GetKey<RecordType> = (record: RecordType, index?: number) => NTree.TKey;

export function convertDataToEntities(
  dataNodes: NTree.IDataNode[],
  {
    initWrapper,
    processEntity,
    onProcessFinished,
    externalGetKey,
    childrenPropName,
    fieldNames
  }: {
    initWrapper?: (wrapper: IWrapper) => IWrapper;
    processEntity?: (entity: NTree.IDataEntity, wrapper: IWrapper) => void;
    onProcessFinished?: (wrapper: IWrapper) => void;
    externalGetKey?: TExternalGetKey;
    childrenPropName?: string;
    fieldNames?: NTree.IFieldNames;
  } = {}
) {
  const posEntities: any = {};
  const keyEntities: any = {};

  let wrapper = { posEntities, keyEntities };

  if (initWrapper) {
    wrapper = initWrapper(wrapper) || wrapper;
  }

  traverseDataNodes(
    dataNodes,
    (item) => {
      const { node, index, pos, key, parentPos, level, nodes } = item;
      const entity: NTree.IDataEntity = { node, index, key, pos, level, nodes };

      const mergedKey = getKey(key, pos);

      posEntities[pos] = entity;
      keyEntities[mergedKey] = entity;

      entity.parent = posEntities[parentPos as string];

      if (entity.parent) {
        entity.parent.children = entity.parent.children || [];
        entity.parent.children.push(entity);
      }

      if (processEntity) {
        processEntity(entity, wrapper);
      }
    },
    { externalGetKey, childrenPropName, fieldNames }
  );

  if (onProcessFinished) {
    onProcessFinished(wrapper);
  }

  return wrapper;
}

/**
 * Convert `children` of Tree into `treeData` structure.
 */
export function convertTreeToData(rootNodes: VueNode): NTree.IDataNode[] {
  function dig(node: VueNode = []): NTree.IDataNode[] {
    const treeNodes = filterEmpty(node as NTree.TNodeElement[]);
    return treeNodes.map((treeNode) => {
      // Filter invalidate node
      if (!isTreeNode(treeNode)) {
        if (!treeNode) warn('CdsTree', 'Tree/TreeNode can only accept TreeNode as children.');
        return null as unknown as any;
      }

      const slots = (treeNode.children as any) || {};
      const key = treeNode.key as string | number;
      const props: any = {};
      for (const [k, v] of Object.entries(treeNode.props)) {
        props[camelize(k)] = v;
      }
      const { isLeaf, checkable, selectable, disabled, disableCheckbox } = props;
      const newProps = {
        isLeaf: isLeaf || isLeaf === '' || undefined,
        checkable: checkable || checkable === '' || undefined,
        selectable: selectable || selectable === '' || undefined,
        disabled: disabled || disabled === '' || undefined,
        disableCheckbox: disableCheckbox || disableCheckbox === '' || undefined
      };
      const slotsProps = { ...props, ...newProps };
      const {
        title = slots.title?.(slotsProps),
        icon = slots.icon?.(slotsProps),
        switcherIcon = slots.switcherIcon?.(slotsProps),
        ...rest
      } = props;
      const children = slots.default?.();
      const dataNode: NTree.IDataNode = {
        ...rest,
        title,
        icon,
        switcherIcon,
        key,
        ...newProps
      };

      const parsedChildren = dig(children);
      if (parsedChildren.length) {
        dataNode.children = parsedChildren;
      }

      return dataNode;
    });
  }
  return dig(rootNodes);
}

interface ITraverseConfig {
  childrenPropName?: string;
  externalGetKey?: TExternalGetKey;
  fieldNames?: NTree.IFieldNames;
}

export function traverseDataNodes(
  dataNodes: NTree.IDataNode[],
  callback: (data: {
    node: NTree.IDataNode;
    index?: number;
    pos: string;
    key: NTree.TKey;
    parentPos?: string | number | null;
    level?: number;
    nodes: NTree.IDataNode[];
  }) => void,
  config?: ITraverseConfig | string
) {
  let mergedConfig: ITraverseConfig = {};

  if (typeof config === 'object') {
    mergedConfig = config;
  } else {
    mergedConfig = { externalGetKey: config };
  }

  mergedConfig = mergedConfig || {};

  const { childrenPropName, externalGetKey, fieldNames } = mergedConfig;
  const { key: fieldKey, children: fieldChildren } = fillFieldNames(fieldNames);
  const mergeChildrenPropName = childrenPropName || fieldChildren;

  let syntheticGenKey: (node: NTree.IDataNode, pos?: string) => NTree.TKey;

  if (externalGetKey) {
    if (typeof externalGetKey === 'string') {
      syntheticGenKey = (node: NTree.IDataNode) => (node as any)[externalGetKey as string];
    } else if (typeof externalGetKey === 'function') {
      syntheticGenKey = (node: NTree.IDataNode) => (externalGetKey as GetKey<NTree.IDataNode>)(node);
    }
  } else {
    syntheticGenKey = (node: NTree.IDataNode, pos?: string) => getKey(node[fieldKey], pos as string);
  }

  const processNode = (
    node: NTree.IDataNode | null,
    index?: number,
    parent?: { node: NTree.IDataNode | null; pos: string; level: number },
    pathNodes?: NTree.IDataNode[]
  ) => {
    const children: NTree.IDataNode[] = node ? node[mergeChildrenPropName] : dataNodes;
    const pos = node ? getPosition(parent && parent.pos, index) : '0';
    const connectNodes = node ? [...(pathNodes || []), node] : [];

    if (node) {
      const key: NTree.TKey = syntheticGenKey(node, pos);

      callback({
        node,
        index,
        pos,
        key,
        parentPos: parent && parent.node ? parent.pos : null,
        level: parent && parent.level + 1,
        nodes: connectNodes
      });
    }

    if (children) {
      children.forEach((subNode, subIndex) => {
        processNode(subNode, subIndex, { node, pos, level: parent ? parent.level + 1 : -1 }, connectNodes);
      });
    }
  };

  processNode(null);
}

/**
 * If user use `autoExpandParent` we should get the list of parent node
 * @param keyList
 * @param keyEntities
 */
export function conductExpandParent(
  keyList: NTree.TKey[],
  keyEntities: Record<NTree.TKey, NTree.IDataEntity>
): NTree.TKey[] {
  const expandedKeys = new Set<NTree.TKey>();

  function conductUp(key: NTree.TKey) {
    if (expandedKeys.has(key)) return;

    const entity = keyEntities[key];
    if (!entity) return;

    expandedKeys.add(key);

    const { parent, node } = entity;

    if (node.disabled) return;

    if (parent) {
      conductUp(parent.key);
    }
  }

  (keyList || []).forEach((key) => {
    conductUp(key);
  });

  return [...expandedKeys];
}

/**
 * Flat nest tree data into flatten list. This is used for virtual list render.
 * @param treeNodeList Origin data node list
 * @param expandedKeys
 * @param fieldNames
 * need expanded keys, provides `true` means all expanded.
 */
export function flattenTreeData(
  treeNodeList: NTree.IDataNode[],
  expandedKeys: NTree.TKey[] | true,
  fieldNames: NTree.IFieldNames
): NTree.IFlattenNode[] {
  const { _title: fieldTitles, key: fieldKey, children: fieldChildren } = fillFieldNames(fieldNames);

  const expandedKeySet = new Set(expandedKeys === true ? [] : expandedKeys);
  const flattenList: NTree.IFlattenNode[] = [];

  function dig(list: NTree.IDataNode[], parent: NTree.IFlattenNode | null = null): NTree.IFlattenNode[] {
    return list.map((treeNode, index) => {
      const pos: string = getPosition(parent ? parent.pos : '0', index);
      const mergedKey = getKey(treeNode[fieldKey], pos);

      // Pick matched title in field title list
      let mergedTitle: any;
      for (let i = 0; i < fieldTitles.length; i += 1) {
        const fieldTitle = fieldTitles[i];
        if (treeNode[fieldTitle] !== undefined) {
          mergedTitle = treeNode[fieldTitle];
          break;
        }
      }
      // Add FlattenDataNode into list
      const flattenNode: NTree.IFlattenNode = {
        ...omit(treeNode, [...fieldTitles, fieldKey, fieldChildren] as any),
        title: mergedTitle,
        key: mergedKey,
        parent,
        pos,
        children: null,
        data: treeNode,
        isStart: [...(parent ? parent.isStart : []), index === 0],
        isEnd: [...(parent ? parent.isEnd : []), index === list.length - 1]
      };

      flattenList.push(flattenNode);

      // Loop treeNode children
      if (expandedKeys === true || expandedKeySet.has(mergedKey)) {
        flattenNode.children = dig(treeNode[fieldChildren] || [], flattenNode);
      } else {
        flattenNode.children = [];
      }

      return flattenNode;
    });
  }

  dig(treeNodeList);

  return flattenList;
}

export function fillFieldNames(fieldNames?: NTree.IFieldNames): Required<NTree.IFieldNames> {
  const { title, _title, key, children } = fieldNames || {};
  const mergedTitle = title || 'title';

  return {
    title: mergedTitle,
    _title: _title || [mergedTitle],
    key: key || 'key',
    children: children || 'children'
  };
}

/**
 * Get TreeNode props with Tree props.
 */
export function getTreeNodeProps<TreeDataType extends NTree.IBasicDataNode = NTree.IDataNode>(
  key: NTree.TKey,
  {
    expandedKeysSet,
    selectedKeysSet,
    loadedKeysSet,
    loadingKeysSet,
    checkedKeysSet,
    halfCheckedKeysSet,
    dragOverNodeKey,
    dropPosition,
    keyEntities
  }: NTree.ITreeNodeRequiredProps<TreeDataType>
) {
  const entity = keyEntities[key];

  return {
    eventKey: key,
    expanded: expandedKeysSet.has(key),
    selected: selectedKeysSet.has(key),
    loaded: loadedKeysSet.has(key),
    loading: loadingKeysSet.has(key),
    checked: checkedKeysSet.has(key),
    halfChecked: halfCheckedKeysSet.has(key),
    pos: String(entity ? entity.pos : ''),
    parent: entity.parent,
    dragOver: dragOverNodeKey === key && dropPosition === 0,
    dragOverGapTop: dragOverNodeKey === key && dropPosition === -1,
    dragOverGapBottom: dragOverNodeKey === key && dropPosition === 1
  };
}

// Only used when drag, not affect SSR.
export function calcDropPosition<TreeDataType extends NTree.IBasicDataNode = NTree.IDataNode>(
  event: MouseEvent,
  dragNode: NTree.TDragNodeEvent,
  targetNode: NTree.TDragNodeEvent,
  indent: number,
  startMousePosition: {
    x: number;
    y: number;
  },
  allowDrop: NTree.TAllowDrop<TreeDataType>,
  flattenedNodes: NTree.IFlattenNode[],
  keyEntities: Record<NTree.TKey, NTree.IDataEntity<TreeDataType>>,
  expandKeysSet: Set<NTree.TKey>
): Omit<NTree.IDragState, 'draggingNodeKey' | 'dragChildrenKeys'> {
  const { clientX, clientY } = event;
  const { top, height } = (event.target as HTMLElement).getBoundingClientRect();
  // optional chain for testing
  const horizontalMouseOffset = (startMousePosition?.x || 0) - clientX;
  const rawDropLevelOffset = (horizontalMouseOffset - 12) / indent;

  // find abstract drop node by horizontal offset
  let abstractDropNodeEntity: NTree.IDataEntity<TreeDataType> | undefined = keyEntities[targetNode.eventKey];

  if (clientY < top + height / 2) {
    // first half, set abstract drop node to previous node
    const nodeIndex = flattenedNodes.findIndex((flattenedNode) => flattenedNode.key === abstractDropNodeEntity?.key);
    const prevNodeIndex = nodeIndex <= 0 ? 0 : nodeIndex - 1;
    const prevNodeKey = flattenedNodes[prevNodeIndex].key;
    abstractDropNodeEntity = keyEntities[prevNodeKey];
  }

  const initialAbstractDropNodeKey = abstractDropNodeEntity.key;

  const abstractDragOverEntity = abstractDropNodeEntity;
  const dragOverNodeKey = abstractDropNodeEntity.key;

  let dropPosition: -1 | 0 | 1 = 0;
  let dropLevelOffset = 0;

  // Only allow cross level drop when dragging on a non-expanded node
  if (!expandKeysSet.has(initialAbstractDropNodeKey)) {
    for (let i = 0; i < rawDropLevelOffset; i += 1) {
      if (isLastChild(abstractDropNodeEntity)) {
        abstractDropNodeEntity = abstractDropNodeEntity?.parent;
        dropLevelOffset += 1;
      } else {
        break;
      }
    }
  }
  const abstractDragDataNode = dragNode.eventData;
  const abstractDropDataNode = abstractDropNodeEntity?.node;
  let dropAllowed = true;
  if (
    isFirstChild(abstractDropNodeEntity) &&
    abstractDropNodeEntity?.level === 0 &&
    clientY < top + height / 2 &&
    allowDrop({
      dragNode: abstractDragDataNode!,
      dropNode: abstractDropDataNode!,
      dropPosition: -1
    }) &&
    abstractDropNodeEntity.key === targetNode.eventKey
  ) {
    // first half of first node in first level
    dropPosition = -1;
  } else if ((abstractDragOverEntity.children || []).length && expandKeysSet.has(dragOverNodeKey)) {
    // drop on expanded node
    // only allow drop inside
    if (
      allowDrop({
        dragNode: abstractDragDataNode!,
        dropNode: abstractDropDataNode!,
        dropPosition: 0
      })
    ) {
      dropPosition = 0;
    } else {
      dropAllowed = false;
    }
  } else if (dropLevelOffset === 0) {
    if (rawDropLevelOffset > -1.5) {
      // | Node     | <- abstractDropNode
      // | -^-===== | <- mousePosition
      // 1. try drop after
      // 2. do not allow drop
      if (
        allowDrop({
          dragNode: abstractDragDataNode!,
          dropNode: abstractDropDataNode!,
          dropPosition: 1
        })
      ) {
        dropPosition = 1;
      } else {
        dropAllowed = false;
      }
    } else {
      // | Node     | <- abstractDropNode
      // | ---==^== | <- mousePosition
      // whether it has children or doesn't has children
      // always
      // 1. try drop inside
      // 2. try drop after
      // 3. do not allow drop
      if (
        allowDrop({
          dragNode: abstractDragDataNode!,
          dropNode: abstractDropDataNode!,
          dropPosition: 0
        })
      ) {
        dropPosition = 0;
      } else if (
        allowDrop({
          dragNode: abstractDragDataNode!,
          dropNode: abstractDropDataNode!,
          dropPosition: 1
        })
      ) {
        dropPosition = 1;
      } else {
        dropAllowed = false;
      }
    }
  } else {
    // | Node1 | <- abstractDropNode
    //      |  Node2  |
    // --^--|----=====| <- mousePosition
    // 1. try insert after Node1
    // 2. do not allow drop
    if (
      allowDrop({
        dragNode: abstractDragDataNode!,
        dropNode: abstractDropDataNode!,
        dropPosition: 1
      })
    ) {
      dropPosition = 1;
    } else {
      dropAllowed = false;
    }
  }

  return {
    dropPosition,
    dropLevelOffset,
    dropTargetKey: abstractDropNodeEntity!.key,
    dropTargetPos: abstractDropNodeEntity!.pos,
    dragOverNodeKey,
    dropContainerKey: dropPosition === 0 ? null : abstractDropNodeEntity?.parent?.key || null,
    dropAllowed
  };
}

/**
 * Return selectedKeys according with multiple prop
 * @param selectedKeys
 * @param props
 * @returns [string]
 */
export function calcSelectedKeys(selectedKeys: NTree.TKey[], props: ITreeProps) {
  if (!selectedKeys) return undefined;

  const { multiple } = props;
  if (multiple) {
    return selectedKeys.slice();
  }

  if (selectedKeys.length) {
    return [selectedKeys[0]];
  }
  return selectedKeys;
}

/**
 * Parse `checkedKeys` to { checkedKeys, halfCheckedKeys } style
 */
export function parseCheckedKeys(keys: NTree.TKey[] | { checked: NTree.TKey[]; halfChecked: NTree.TKey[] }) {
  if (!keys) {
    return null;
  }

  // Convert keys to object format
  let keyProps;
  if (Array.isArray(keys)) {
    // [Legacy] Follow the api doc
    keyProps = {
      checkedKeys: keys,
      halfCheckedKeys: undefined
    };
  } else if (typeof keys === 'object') {
    keyProps = {
      checkedKeys: keys.checked || undefined,
      halfCheckedKeys: keys.halfChecked || undefined
    };
  } else {
    warn('CdsTree', '`checkedKeys` is not an array or an object');
    return null;
  }

  return keyProps;
}

export function getDragChildrenKeys<TreeDataType extends NTree.IBasicDataNode = NTree.IDataNode>(
  dragNodeKey: NTree.TKey,
  keyEntities: Record<NTree.TKey, NTree.IDataEntity<TreeDataType>>
): NTree.TKey[] {
  // not contains self
  // for left or right drag
  const dragChildrenKeys: NTree.TKey[] = [];

  const entity = keyEntities[dragNodeKey];
  function dig(list: NTree.IDataEntity<TreeDataType>[] = []) {
    list.forEach(({ key, children }) => {
      dragChildrenKeys.push(key);
      dig(children);
    });
  }

  dig(entity.children);

  return dragChildrenKeys;
}

export function getKey(key: NTree.TKey, pos: string) {
  if (key !== null && key !== undefined) {
    return key;
  }

  return pos;
}

export function getPosition(level?: string | number, index?: number) {
  return `${level}-${index}`;
}

export function isTreeNode(node: NTree.TNodeElement) {
  return node && node.type && (node.type as any).isTreeNode;
}

export function arrDel(list: NTree.TKey[], value: NTree.TKey) {
  if (!list) return [];
  const clone = list.slice();
  const index = clone.indexOf(value);
  if (index >= 0) {
    clone.splice(index, 1);
  }
  return clone;
}

export function arrAdd(list: NTree.TKey[], value: NTree.TKey) {
  const clone = (list || []).slice();
  if (clone.indexOf(value) === -1) {
    clone.push(value);
  }
  return clone;
}

export function posToArr(pos: string) {
  return pos.split('-');
}

export function isLastChild<TreeDataType extends NTree.IBasicDataNode = NTree.IDataNode>(
  treeNodeEntity?: NTree.IDataEntity<TreeDataType>
) {
  if (treeNodeEntity?.parent) {
    const posArr = posToArr(treeNodeEntity.pos);
    return Number(posArr[posArr.length - 1]) === treeNodeEntity.parent.children!.length - 1;
  }
  return false;
}

export function isFirstChild<TreeDataType extends NTree.IBasicDataNode = NTree.IDataNode>(
  treeNodeEntity?: NTree.IDataEntity<TreeDataType>
) {
  const posArr = posToArr(treeNodeEntity!.pos);
  return Number(posArr[posArr.length - 1]) === 0;
}

export function convertNodePropsToEventData(
  props: ITreeNodeProps & ReturnType<typeof getTreeNodeProps>
): NTree.IEventDataNode {
  const {
    data,
    expanded,
    selected,
    checked,
    loaded,
    loading,
    halfChecked,
    dragOver,
    dragOverGapTop,
    dragOverGapBottom,
    pos,
    active,
    eventKey
  } = props;
  const eventData = {
    dataRef: data,
    ...data,
    expanded,
    selected,
    checked,
    loaded,
    loading,
    halfChecked,
    dragOver,
    dragOverGapTop,
    dragOverGapBottom,
    pos,
    active,
    eventKey,
    key: eventKey
  };
  if (!('props' in eventData)) {
    Object.defineProperty(eventData, 'props', {
      get() {
        warn(
          'CdsTree',
          'Second param return from event is node data instead of TreeNode instance. Please read value directly instead of reading from `props`.'
        );
        return props;
      }
    });
  }

  return eventData;
}
